import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";

import { ReloadOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { uniq } from "lodash";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../../Common/UIComponents/SelectPopupScrollbar";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Customized,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import DatePicker from "../../../../Common/DatePicker/DatePicker";
import Loading from "../../../../Common/Loading";
import NoResultsGif from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import LockedContent from "../../../../grace-period/components/LockedContent";
import { useAccessStatus } from "../../../../grace-period/hooks/useAccessStatus";
import TrendChartPill from "../VolumeTrend/TrendChartPill";
import ResultTestAnalyticsTooltip from "./ResultTestAnalyticsTooltip";
import ResultTestSampleModal from "./ResultTestSampleModal";

import {
  buildResultTestSampleFetchParams,
  buildResultTestSampleModalTitle,
} from "../../utils";

import { getEnvironmentSecondaryFields, getMapTitles } from "../../../../../actions/envAnalytics";

import { ReactComponent as CalendarIcon } from "../../../../../assets/images/environment/CalendarEnv.svg";
import pumpkinGif from "../../../../../assets/images/environment/pumpkin.gif";

import "./ResultTestAnalyticsChart.css";

const DEFAULT_VISIBLE_BARS = 8;
const ALL_MAPS_VALUE = "__all_maps__";
const formatMapDisplayName = (mapName) => (
  mapName === "no_map" ? "No map" : mapName
);
// Hex values used for SVG fills (CSS vars are unreliable in Recharts)
const RESULTS_COLOR = "#6649ff";
const REQUESTED_COLOR = "#c82a8b";
const STACKED_BAR_RADIUS = 10;

const getStackedBarRadius = (entry, segment) => {
  const inProcess = entry.testsInProcess;
  const available = entry.resultsAvailable;

  if (segment === "pending") {
    return inProcess > 0 ? [STACKED_BAR_RADIUS, STACKED_BAR_RADIUS, 0, 0] : [0, 0, 0, 0];
  }

  return inProcess === 0 && available > 0
    ? [STACKED_BAR_RADIUS, STACKED_BAR_RADIUS, 0, 0]
    : [0, 0, 0, 0];
};

const ResultsBarShape = (props) => {
  const {
    x, y, width, height, fill, payload,
  } = props;

  if (!height || height <= 0) return null;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      radius={getStackedBarRadius(payload, "available")}
    />
  );
};

const PendingBarShape = (props) => {
  const {
    x, y, width, height, fill, payload,
  } = props;

  if (!height || height <= 0) return null;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      radius={getStackedBarRadius(payload, "pending")}
    />
  );
};

const getChartGroupKey = (entry, selectedSecondaryField, selectedMapValue) => {
  if (selectedSecondaryField) {
    const secondaryValue = entry.secondaryFieldValue ?? "";

    if (selectedMapValue === ALL_MAPS_VALUE) {
      return `${entry.mapName}__${secondaryValue}`;
    }
    return String(secondaryValue);
  }

  return entry.mapName;
};

const getChartGroupLabel = (groupKey, selectedSecondaryField, selectedMapValue) => {
  if (selectedSecondaryField) {
    if (selectedMapValue === ALL_MAPS_VALUE) {
      const separatorIdx = groupKey.indexOf("__");
      if (separatorIdx === -1) return groupKey;

      const mapName = groupKey.slice(0, separatorIdx);
      const secondaryValue = groupKey.slice(separatorIdx + 2);
      return `${formatMapDisplayName(mapName)} - ${secondaryValue}`;
    }
    return groupKey || "-";
  }

  return formatMapDisplayName(groupKey);
};

const X_AXIS_TICK_HEIGHT = 44;

const buildChartGroups = (chartData, selectedSecondaryField, selectedMapValue) => {
  if (!chartData.length) return [];

  const groups = [];
  chartData.forEach((entry, index) => {
    const groupKey = getChartGroupKey(entry, selectedSecondaryField, selectedMapValue);
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || lastGroup.groupKey !== groupKey) {
      groups.push({
        groupKey,
        groupLabel: getChartGroupLabel(groupKey, selectedSecondaryField, selectedMapValue),
        endIdx: index,
        barCount: 1,
      });
    } else {
      lastGroup.endIdx = index;
      lastGroup.barCount += 1;
    }
  });

  return groups;
};

const ChartGroupSeparators = ({
  xAxisMap, offset, height, groups, chartData,
}) => {
  const axis = xAxisMap ? Object.values(xAxisMap)[0] : null;
  if (!axis?.scale || !axis?.bandwidth || groups.length <= 1) return null;

  return (
    <g>
      {groups.slice(0, -1).map((group) => {
        const lastEntry = chartData[group.endIdx];
        const x = axis.scale(lastEntry.barKey) + axis.bandwidth;
        return (
          <line
            key={`separator-${group.groupKey}`}
            x1={x}
            y1={offset.top}
            x2={x}
            y2={offset.top + height}
            stroke="var(--color-border-secondary)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        );
      })}
    </g>
  );
};

const GroupAxisRow = ({ groups, fieldLabel }) => {
  if (!groups.length) return null;

  return (
    <div className="ResultTestAnalyticsChart__GroupAxisRow">
      {fieldLabel && (
        <span className="ResultTestAnalyticsChart__GroupAxisFieldLabel">{fieldLabel}</span>
      )}
      <div className="ResultTestAnalyticsChart__GroupAxisTrack">
        {groups.map((group) => (
          <div
            key={group.groupKey}
            className="ResultTestAnalyticsChart__GroupAxisCell"
            style={{ flex: group.barCount }}
          >
            <div className="ResultTestAnalyticsChart__GroupBracket" aria-hidden>
              <span className="ResultTestAnalyticsChart__GroupBracketLine" />
            </div>
            <span className="ResultTestAnalyticsChart__GroupLabel" title={group.groupLabel}>
              {group.groupLabel}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MAX_TICK_LABEL_LENGTH = 14;

const renderTestNameTick = ({ x, y, payload }) => {
  const parts = (payload.value ?? "").split("__");
  const testName = parts[parts.length - 1] || payload.value || "";
  const displayName = testName.length > MAX_TICK_LABEL_LENGTH
    ? `${testName.slice(0, MAX_TICK_LABEL_LENGTH)}…`
    : testName;

  return (
    <text
      x={x}
      y={y + 14}
      textAnchor="middle"
      fill="var(--color-content-secondary)"
      fontSize={11}
      fontFamily="Roboto"
    >
      <title>{testName}</title>
      {displayName}
    </text>
  );
};

const ResultTestAnalyticsChart = ({
  mapTestDetails = null,
  isLoading = false,
  dateRange = "",
  selectedMap = null,
  selectedSecondaryField = null,
  onMapChange,
  onSecondaryFieldChange,
  onDateChange,
  onDateSearch,
  onReset,
  prevDateRange = "",
  setPrevDateRange,
  getReportDates,
}) => {
  const { areResultsLocked } = useAccessStatus();
  const selectedMapValue = selectedMap ?? ALL_MAPS_VALUE;
  const [mapTitles, setMapTitles] = useState([]);
  const [secondaryFields, setSecondaryFields] = useState([]);
  const [isLoadingMaps, setIsLoadingMaps] = useState(true);
  const [isLoadingSecondaryFields, setIsLoadingSecondaryFields] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadMapTitles = async () => {
      setIsLoadingMaps(true);
      const { success, mapTitles: titles, message } = await getMapTitles();

      if (!isMounted) return;

      if (success) {
        setMapTitles(titles);
      } else {
        toast.error(message || "Failed to load maps");
        setMapTitles([]);
      }
      setIsLoadingMaps(false);
    };

    const loadSecondaryFields = async () => {
      setIsLoadingSecondaryFields(true);
      const {
        success,
        secondaryFields: fields,
        message,
      } = await getEnvironmentSecondaryFields();

      if (!isMounted) return;

      if (success) {
        setSecondaryFields(fields);
      } else {
        toast.error(message || "Failed to load secondary fields");
        setSecondaryFields([]);
      }
      setIsLoadingSecondaryFields(false);
    };

    loadMapTitles();
    loadSecondaryFields();

    return () => {
      isMounted = false;
    };
  }, []);
  const allTestNames = useMemo(
    () => uniq(mapTestDetails.map((entry) => entry.testName)),
    [mapTestDetails],
  );

  const [selectedTests, setSelectedTests] = useState([]);
  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: DEFAULT_VISIBLE_BARS - 1 });
  const [sampleModal, setSampleModal] = useState({
    open: false,
    fetchParams: null,
    title: "",
    summaryCounts: {
      total: 0,
      resultsAvailable: 0,
      inProcess: 0,
    },
  });

  useEffect(() => {
    if (allTestNames.length > 0) {
      setSelectedTests(allTestNames);
    } else {
      setSelectedTests([]);
    }
  }, [allTestNames]);

  const filteredChartData = useMemo(() => {
    let data = mapTestDetails.filter((entry) => selectedTests.includes(entry.testName));

    if (selectedMapValue !== ALL_MAPS_VALUE) {
      data = data.filter((entry) => entry.mapName === selectedMapValue);
    }

    return data;
  }, [mapTestDetails, selectedTests, selectedMapValue]);

  const showBrush = filteredChartData.length > DEFAULT_VISIBLE_BARS;

  useEffect(() => {
    const lastIndex = Math.max(0, filteredChartData.length - 1);
    const endIndex = Math.min(DEFAULT_VISIBLE_BARS - 1, lastIndex);
    setBrushRange({ startIndex: 0, endIndex });
  }, [filteredChartData]);

  const visibleChartData = useMemo(() => {
    if (!showBrush) return filteredChartData;

    const { startIndex, endIndex } = brushRange;
    return filteredChartData.slice(startIndex, endIndex + 1);
  }, [filteredChartData, brushRange, showBrush]);

  const visibleRangeLabel = useMemo(() => {
    const total = filteredChartData.length;
    if (total === 0) return "";

    if (!showBrush) {
      return `${total} bar${total === 1 ? "" : "s"}`;
    }

    const { startIndex, endIndex } = brushRange;
    return `Bars ${startIndex + 1}–${endIndex + 1} of ${total}`;
  }, [filteredChartData.length, brushRange, showBrush]);

  const visibleChartGroups = useMemo(
    () => buildChartGroups(
      visibleChartData,
      selectedSecondaryField,
      selectedMapValue,
    ),
    [visibleChartData, selectedSecondaryField, selectedMapValue],
  );

  const handleBrushChange = useCallback((range) => {
    if (range?.startIndex == null || range?.endIndex == null) return;
    setBrushRange({ startIndex: range.startIndex, endIndex: range.endIndex });
  }, []);

  const secondaryFieldLabel = useMemo(() => {
    if (!selectedSecondaryField) return null;
    const field = secondaryFields.find((item) => item.json_field === selectedSecondaryField);
    return field?.title_field ?? null;
  }, [secondaryFields, selectedSecondaryField]);

  const showGroupAxis = visibleChartGroups.length > 0;
  const chartBottomMargin = X_AXIS_TICK_HEIGHT + 12;

  const brushOverviewData = useMemo(
    () => filteredChartData.map((entry) => ({
      ...entry,
      brushTotal: (entry.resultsAvailable ?? 0) + (entry.testsInProcess ?? 0),
    })),
    [filteredChartData],
  );

  const selectedMapLabel = selectedMapValue === ALL_MAPS_VALUE
    ? "All maps"
    : formatMapDisplayName(selectedMapValue);

  const handleMapSelect = useCallback((value) => {
    const mapTitle = (value === ALL_MAPS_VALUE || value == null) ? null : value;
    if (onMapChange) {
      onMapChange(mapTitle);
    }
  }, [onMapChange]);

  const handleSecondaryFieldSelect = useCallback((value) => {
    if (onSecondaryFieldChange) {
      onSecondaryFieldChange(value ?? null);
    }
  }, [onSecondaryFieldChange]);

  const handleTestToggle = useCallback((testName) => {
    setSelectedTests((prev) => (
      prev.includes(testName)
        ? prev.filter((name) => name !== testName)
        : [...prev, testName]
    ));
  }, []);

  const handleBarClick = useCallback((entry) => {
    if (!entry || !dateRange) return;

    setSampleModal({
      open: true,
      fetchParams: buildResultTestSampleFetchParams({
        entry,
        dateRange,
        secondaryField: selectedSecondaryField,
      }),
      title: buildResultTestSampleModalTitle(entry),
      summaryCounts: {
        total: entry.testsRequested ?? 0,
        resultsAvailable: entry.resultsAvailable ?? 0,
        inProcess: entry.testsInProcess ?? 0,
      },
    });
  }, [dateRange, selectedSecondaryField]);

  const handleCloseSampleModal = useCallback(() => {
    setSampleModal({
      open: false,
      fetchParams: null,
      title: "",
      summaryCounts: {
        total: 0,
        resultsAvailable: 0,
        inProcess: 0,
      },
    });
  }, []);

  const renderTooltip = useCallback((tooltipProps) => (
    <ResultTestAnalyticsTooltip {...tooltipProps} onViewDetails={handleBarClick} />
  ), [handleBarClick]);

  const mapSelectOptions = useMemo(() => {
    const mapOptions = mapTitles.map((map) => ({
      label: map.title,
      value: map.title,
    }));

    return [
      { label: "All maps", value: ALL_MAPS_VALUE },
      ...mapOptions,
    ];
  }, [mapTitles]);

  return (
    <div className="ResultTestAnalyticsChart">
      <div className="ResultTestAnalyticsChart__TitleBar">
        Result and Test Analytics
      </div>

      <div className="ResultTestAnalyticsChart__Toolbar">
        <div className="ResultTestAnalyticsChart__Filters">
          <div className="ResultTestAnalyticsChart__MapField">
            <span className="ResultTestAnalyticsChart__MapFieldLabel">Select a map</span>
            <Select
              className="ResultTestAnalyticsChart__MapSelect"
              value={selectedMapValue}
              onChange={handleMapSelect}
              options={mapSelectOptions}
              loading={isLoadingMaps}
              allowClear={selectedMapValue !== ALL_MAPS_VALUE}
              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
              classNames={selectPopupClassNames("ResultTestAnalyticsChart__MapSelectDropdown")}
              virtual={false}
              listHeight={256}
              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                optionCount: mapSelectOptions.length,
              })}
            />
          </div>
          <div className="ResultTestAnalyticsChart__SecondaryField">
            <span className="ResultTestAnalyticsChart__MapFieldLabel">Select a secondary field</span>
            <Select
              className="ResultTestAnalyticsChart__MapSelect"
              value={selectedSecondaryField}
              onChange={handleSecondaryFieldSelect}
              options={secondaryFields}
              fieldNames={{ label: "title_field", value: "json_field" }}
              loading={isLoadingSecondaryFields}
              placeholder="Choose field"
              allowClear
              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
              classNames={selectPopupClassNames("ResultTestAnalyticsChart__MapSelectDropdown")}
              virtual={false}
              listHeight={256}
              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                optionCount: (secondaryFields || []).length,
              })}
            />
          </div>
        </div>
        {onDateSearch && (
          <DatePicker
            handleSearchDate={onDateSearch}
            value={dateRange}
            onChange={onDateChange}
            getReportDates={getReportDates}
            searchOnClear
            prevValue={prevDateRange}
            setPrevValue={setPrevDateRange}
            popoverPlacement="bottomRight"
            popoverClassName="EnvAnalyticsTabPopover"
            disableClickAway
            disableSingleDaySelection
            allowClear
            datePickerDisplayProps={{
              displayClassName: "ResultTestAnalyticsChart__DateRange",
              customCalendarIcon: <CalendarIcon width={18} height={18} />,
              showText: true,
            }}
          />
        )}
      </div>

      {allTestNames.length > 0 && (
        <div className="ResultTestAnalyticsChart__Controls">
          <div className="ResultTestAnalyticsChart__PillsWrap">
            <StyledScrollbar
              noScrollY
              autoHide="x"
              disableTrackXWidthCompensation
              className="ResultTestAnalyticsChart__PillsScrollbar"
            >
              <div className="ResultTestAnalyticsChart__Pills">
                {allTestNames.map((testName) => (
                  <TrendChartPill
                    key={testName}
                    testName={testName}
                    isSelected={selectedTests.includes(testName)}
                    onClick={handleTestToggle}
                  />
                ))}
              </div>
            </StyledScrollbar>
          </div>
          <div className="ResultTestAnalyticsChart__Legend">
            <div className="ResultTestAnalyticsChart__LegendItem">
              <span
                className="ResultTestAnalyticsChart__LegendSwatch ResultTestAnalyticsChart__LegendSwatch--requested"
              />
              <span>Tests in process</span>
            </div>
            <div className="ResultTestAnalyticsChart__LegendItem">
              <span
                className="ResultTestAnalyticsChart__LegendSwatch ResultTestAnalyticsChart__LegendSwatch--available"
              />
              <span>Results available</span>
            </div>
          </div>
        </div>
      )}

      <div className="ResultTestAnalyticsChart__InfoBar">
        <span>
          Currently displaying details for
          {" "}
          {selectedMapLabel}
          .
        </span>
        {onReset && (
          <button
            type="button"
            className="ResultTestAnalyticsChart__Reset"
            onClick={onReset}
          >
            <ReloadOutlined className="ResultTestAnalyticsChart__ResetIcon" />
            <span className="ResultTestAnalyticsChart__ResetLabel">Reset</span>
          </button>
        )}
      </div>

      <div className={`ResultTestAnalyticsChart__Body${areResultsLocked ? " ResultTestAnalyticsChart__Body--locked" : ""}`}>
        {areResultsLocked && (
          <LockedContent variant="bar" />
        )}
        {!areResultsLocked && isLoading && (
          <div className="ResultTestAnalyticsChart__Loading">
            <Loading />
          </div>
        )}
        {!areResultsLocked && !isLoading && filteredChartData.length > 0 && (
          <div className="ResultTestAnalyticsChart__ChartWrapper">
            <div className="ResultTestAnalyticsChart__ChartArea">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={visibleChartData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 0,
                    bottom: chartBottomMargin,
                  }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke="var(--color-border-secondary)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="barKey"
                    tickLine={false}
                    axisLine={false}
                    tick={renderTestNameTick}
                    interval={0}
                    height={X_AXIS_TICK_HEIGHT}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    tick={{
                      fill: "var(--color-content-secondary)",
                      fontSize: 11,
                      fontFamily: "Roboto",
                    }}
                    domain={[0, "auto"]}
                    width={32}
                  />
                  <Tooltip
                    content={renderTooltip}
                    cursor={{ fill: "rgba(252, 99, 40, 0.06)" }}
                    isAnimationActive={false}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{
                      backgroundColor: "var(--color-bg-primary)",
                      borderRadius: 8,
                      zIndex: 1000,
                    }}
                  />
                  <Bar
                    dataKey="resultsAvailable"
                    stackId="tests"
                    barSize={20}
                    shape={ResultsBarShape}
                    isAnimationActive={false}
                    onClick={(data) => handleBarClick(data?.payload)}
                  >
                    {visibleChartData.map((entry) => (
                      <Cell
                        key={`results-${entry.barKey}`}
                        fill={RESULTS_COLOR}
                      />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="testsInProcess"
                    stackId="tests"
                    barSize={20}
                    shape={PendingBarShape}
                    isAnimationActive={false}
                    onClick={(data) => handleBarClick(data?.payload)}
                  >
                    {visibleChartData.map((entry) => (
                      <Cell
                        key={`pending-${entry.barKey}`}
                        fill={REQUESTED_COLOR}
                      />
                    ))}
                  </Bar>
                  <Customized
                    component={(props) => (
                      <ChartGroupSeparators
                        {...props}
                        groups={visibleChartGroups}
                        chartData={visibleChartData}
                      />
                    )}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {showGroupAxis && (
              <GroupAxisRow
                groups={visibleChartGroups}
                fieldLabel={secondaryFieldLabel}
              />
            )}
            {showBrush && (
              <div className="ResultTestAnalyticsChart__BrushArea">
                <div className="ResultTestAnalyticsChart__BrushHeader">
                  <span className="ResultTestAnalyticsChart__BrushHint">
                    Drag to view more data
                  </span>
                  <span className="ResultTestAnalyticsChart__BrushRange">{visibleRangeLabel}</span>
                </div>
                <ResponsiveContainer width="100%" height={52}>
                  <BarChart
                    data={brushOverviewData}
                    margin={{
                      top: 2,
                      right: 20,
                      left: 0,
                      bottom: 2,
                    }}
                  >
                    <XAxis dataKey="barKey" hide />
                    <YAxis hide width={32} />
                    <Brush
                      dataKey="barKey"
                      startIndex={brushRange.startIndex}
                      endIndex={brushRange.endIndex}
                      onChange={handleBrushChange}
                      height={36}
                      stroke="var(--color-border-secondary)"
                      fill="var(--color-cool-surface)"
                      travellerWidth={10}
                      tickFormatter={() => ""}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
        {!areResultsLocked && !isLoading && !filteredChartData.length && allTestNames.length > 0 && (
          <div className="ResultTestAnalyticsChart__Placeholder" role="alert">
            Please select at least one test to display the graph.
          </div>
        )}
        {!areResultsLocked && !isLoading && !filteredChartData.length && !allTestNames.length && (
          <NoResultsGif
            message="No Data"
            image={pumpkinGif}
            className="ResultTestAnalyticsChart__NoResults"
          />
        )}
      </div>
      <ResultTestSampleModal
        open={sampleModal.open}
        onClose={handleCloseSampleModal}
        dateRange={dateRange}
        fetchParams={sampleModal.fetchParams}
        modalTitle={sampleModal.title}
        summaryCounts={sampleModal.summaryCounts}
      />
    </div>
  );
};

export default ResultTestAnalyticsChart;
